import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { Plus, Trash2, Clock, DollarSign, Edit2, X, Save, Plane, Paperclip, ChevronDown, CircleDollarSign, CheckCircle2, Hourglass } from "lucide-react";
import { API } from "@/contexts/AuthContext";
import AttachmentManager from "./AttachmentManager";

const STATUS_STYLES = {
  unbilled: { label: "Unbilled", bg: "#FCEADB", text: "#A0521A" },
  invoiced: { label: "Invoiced", bg: "#DDE7E9", text: "#284952" },
  paid: { label: "Paid", bg: "#DCEFE0", text: "#1F6B3A" },
  void: { label: "Void", bg: "#E8E8E8", text: "#555" },
};

function StatusPill({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.unbilled;
  return (
    <span className="text-[10px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded-full" style={{ background: s.bg, color: s.text }}>
      {s.label}
    </span>
  );
}

export default function ClientTasksTab({ clientId }) {
  const [tasks, setTasks] = useState([]);
  const [rates, setRates] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, r, s] = await Promise.all([
        axios.get(`${API}/clients/${clientId}/tasks`),
        axios.get(`${API}/ndis-rates`),
        axios.get(`${API}/clients/${clientId}/billing-summary`),
      ]);
      setTasks(t.data || []);
      setRates((r.data || []).filter((x) => x.active));
      setSummary(s.data || null);
    } catch (e) {
      console.error("Failed to load tasks/rates:", e);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const [attachmentsOpen, setAttachmentsOpen] = useState({});
  const toggleAttachments = (id) => setAttachmentsOpen((s) => ({ ...s, [id]: !s[id] }));

  const totals = useMemo(() => {
    if (summary) {
      return {
        unbilled: summary.unbilled || { count: 0, hours: 0, amount: 0 },
        outstanding: summary.outstanding || { count: 0, hours: 0, amount: 0 },
        paid: summary.paid || { count: 0, hours: 0, amount: 0 },
        lifetime: summary.lifetime || { count: 0, hours: 0, amount: 0 },
      };
    }
    // Fallback while summary loads: derive unbilled from local tasks
    const unbilled = tasks.filter((t) => t.status === "unbilled");
    return {
      unbilled: {
        count: unbilled.length,
        hours: unbilled.reduce((s, t) => s + (t.hours || 0), 0),
        amount: unbilled.reduce((s, t) => s + (t.amount || 0), 0),
      },
      outstanding: { count: 0, hours: 0, amount: 0 },
      paid: { count: 0, hours: 0, amount: 0 },
      lifetime: { count: 0, hours: 0, amount: 0 },
    };
  }, [summary, tasks]);

  const deleteTask = async (id) => {
    if (!window.confirm("Delete this billable task? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/tasks/${id}`);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Couldn't delete task.");
    }
  };

  return (
    <div className="bg-white rounded-3xl border border-[#F0E5DE] p-7" data-testid="tasks-tab">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h2 className="font-display text-2xl text-[#1F2A2E] flex items-center gap-3">
            <Clock className="w-5 h-5 text-[#E2718A]" /> Billable tasks
          </h2>
          <p className="text-sm text-[#6B7A80] mt-1">Hours and activities logged against this client at 2026-27 NDIS rates.</p>
        </div>
        <button onClick={() => { setEditing(null); setShowForm(true); }} className="btn-primary text-sm" data-testid="add-task-btn">
          <Plus className="w-4 h-4" /> Log task
        </button>
      </div>

      {/* Billing summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-7" data-testid="billing-summary">
        <SummaryCard
          label="Unbilled"
          amount={totals.unbilled.amount}
          subtitle={`${totals.unbilled.count} task${totals.unbilled.count === 1 ? "" : "s"} · ${totals.unbilled.hours.toFixed(2)} hrs`}
          icon={Hourglass}
          tone="amber"
          testId="summary-unbilled"
        />
        <SummaryCard
          label="Outstanding"
          amount={totals.outstanding.amount}
          subtitle={`${totals.outstanding.count} invoiced, unpaid`}
          icon={CircleDollarSign}
          tone="coral"
          testId="summary-outstanding"
        />
        <SummaryCard
          label="Paid"
          amount={totals.paid.amount}
          subtitle={`${totals.paid.count} task${totals.paid.count === 1 ? "" : "s"} · ${totals.paid.hours.toFixed(2)} hrs`}
          icon={CheckCircle2}
          tone="green"
          testId="summary-paid"
        />
        <SummaryCard
          label="Lifetime billed"
          amount={totals.lifetime.amount}
          subtitle={`${totals.lifetime.count} task${totals.lifetime.count === 1 ? "" : "s"} · ${totals.lifetime.hours.toFixed(2)} hrs`}
          icon={DollarSign}
          tone="navy"
          testId="summary-lifetime"
        />
      </div>

      {loading ? (
        <div className="text-center py-10"><div className="w-7 h-7 border-3 border-[#E2718A] border-t-transparent rounded-full animate-spin mx-auto" /></div>
      ) : tasks.length === 0 ? (
        <div className="text-center py-12 bg-[#F1E4D5] rounded-2xl">
          <Clock className="w-10 h-10 mx-auto mb-3 text-[#F4C3CC]" />
          <h3 className="font-display text-lg text-[#1F2A2E] mb-2">No billable tasks yet</h3>
          <p className="text-sm text-[#6B7A80] mb-5">Log your first hour against this client.</p>
          <button onClick={() => { setEditing(null); setShowForm(true); }} className="btn-primary text-sm">Log a task</button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F1E4D5]">
              <tr className="text-left">
                <Th>Date</Th>
                <Th>Activity</Th>
                <Th>Hours</Th>
                <Th>Rate</Th>
                <Th>Amount</Th>
                <Th>Worker</Th>
                <Th>Status</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F0E5DE]">
              {tasks.map((t) => (
                <FragmentRow
                  key={t.id}
                  task={t}
                  isAttachmentsOpen={!!attachmentsOpen[t.id]}
                  onToggleAttachments={() => toggleAttachments(t.id)}
                  onEdit={() => { setEditing(t); setShowForm(true); }}
                  onDelete={() => deleteTask(t.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <TaskFormModal
          clientId={clientId}
          rates={rates}
          task={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function Th({ children }) {
  return <th className="px-4 py-3 text-xs uppercase tracking-wider font-semibold text-[#6B7A80]">{children}</th>;
}

function SummaryCard({ label, amount, subtitle, icon: Icon, tone = "amber", testId }) {
  const tones = {
    amber: { bg: "bg-[#FCEADB]",  border: "border-[#F4CFA7]", text: "text-[#A0521A]", iconBg: "bg-white" },
    coral: { bg: "bg-[#FCE7EB]",  border: "border-[#F4C3CC]", text: "text-[#A12545]", iconBg: "bg-white" },
    green: { bg: "bg-[#DCEFE0]",  border: "border-[#B9DDBF]", text: "text-[#1F6B3A]", iconBg: "bg-white" },
    navy:  { bg: "bg-[#DDE7E9]",  border: "border-[#B8CACD]", text: "text-[#284952]", iconBg: "bg-white" },
  };
  const t = tones[tone] || tones.amber;
  return (
    <div className={`rounded-2xl p-4 border ${t.bg} ${t.border}`} data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-7 h-7 rounded-lg ${t.iconBg} flex items-center justify-center ${t.text}`}>
          <Icon className="w-3.5 h-3.5" />
        </div>
        <div className={`text-xs uppercase tracking-wider font-semibold ${t.text}`}>{label}</div>
      </div>
      <div className="font-display text-2xl text-[#1F2A2E] tabular-nums">
        ${Number(amount || 0).toFixed(2)}
      </div>
      <div className="text-xs text-[#6B7A80] mt-0.5 truncate">{subtitle}</div>
    </div>
  );
}

function TaskFormModal({ clientId, rates, task, onClose, onSaved }) {
  const isEdit = !!task?.id;
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    activity_date: task?.activity_date || today,
    rate_code: task?.rate_code || (rates[0]?.code || ""),
    hours: task?.hours || 1,
    description: task?.description || "",
    is_travel: task?.is_travel || false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const selectedRate = rates.find((r) => r.code === form.rate_code);
  const computedAmount = selectedRate ? (Number(form.hours) * Number(selectedRate.rate)) : 0;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      if (isEdit) {
        await axios.put(`${API}/tasks/${task.id}`, form);
      } else {
        await axios.post(`${API}/clients/${clientId}/tasks`, form);
      }
      onSaved();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Couldn't save task.");
    } finally {
      setSaving(false);
    }
  };

  // Group rates by category for nicer dropdown
  const grouped = useMemo(() => {
    const m = new Map();
    rates.forEach((r) => {
      if (!m.has(r.category)) m.set(r.category, []);
      m.get(r.category).push(r);
    });
    return Array.from(m.entries());
  }, [rates]);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="task-form-modal">
      <div className="bg-white rounded-[2rem] w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-7 py-5 border-b border-[#F0E5DE]">
          <h2 className="font-display text-2xl text-[#1F2A2E]">{isEdit ? "Edit task" : "Log billable task"}</h2>
          <button onClick={onClose} className="w-10 h-10 rounded-full hover:bg-[#F1E4D5] flex items-center justify-center" aria-label="Close" data-testid="task-modal-close">
            <X className="w-5 h-5 text-[#6B7A80]" />
          </button>
        </div>

        <form onSubmit={submit} className="p-7 space-y-5">
          <div>
            <label className="block text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-1.5">Date of activity</label>
            <input type="date" className="input-navica" value={form.activity_date} onChange={(e) => setForm({ ...form, activity_date: e.target.value })} required data-testid="tf-date" />
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-1.5">NDIS support item</label>
            <select className="input-navica" value={form.rate_code} onChange={(e) => setForm({ ...form, rate_code: e.target.value })} required data-testid="tf-rate-code">
              {grouped.map(([cat, items]) => (
                <optgroup key={cat} label={cat}>
                  {items.map((r) => (
                    <option key={r.code} value={r.code}>
                      {`${r.name}, $${r.rate.toFixed(2)}/${r.unit}`}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            {selectedRate && (
              <p className="text-xs text-[#6B7A80] mt-1.5">
                Code <span className="font-mono">{selectedRate.code}</span> · GST-free · Effective from {selectedRate.effective_from}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-1.5">Hours</label>
              <input type="number" step="0.25" min="0.25" className="input-navica" value={form.hours} onChange={(e) => setForm({ ...form, hours: parseFloat(e.target.value) || 0 })} required data-testid="tf-hours" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-1.5">Computed amount</label>
              <div className="input-navica bg-[#F1E4D5] flex items-center" data-testid="tf-amount">
                <DollarSign className="w-4 h-4 text-[#E2718A] mr-1" />
                <span className="font-semibold text-[#1F2A2E]">{computedAmount.toFixed(2)}</span>
                <span className="text-xs text-[#6B7A80] ml-2">AUD, GST-free</span>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-1.5">Activity notes (will appear on invoice line)</label>
            <textarea rows={3} className="input-navica" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g. Provider liaison call with plan manager re: SDA application" data-testid="tf-description" />
          </div>

          <label className="flex items-center gap-2.5 cursor-pointer">
            <input type="checkbox" checked={form.is_travel} onChange={(e) => setForm({ ...form, is_travel: e.target.checked })} className="navica-check" data-testid="tf-is-travel" />
            <span className="text-sm text-[#284952]">This task is Provider Travel (separate billing line)</span>
          </label>

          {error && (
            <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>
          )}

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-[#F0E5DE]">
            <button type="button" onClick={onClose} className="btn-outline">Cancel</button>
            <button type="submit" disabled={saving || !form.rate_code} className="btn-primary" data-testid="tf-submit">
              {saving ? "Saving..." : isEdit ? "Save changes" : "Log task"} <Save className="w-4 h-4" />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


function FragmentRow({ task, isAttachmentsOpen, onToggleAttachments, onEdit, onDelete }) {
  return (
    <>
      <tr className="hover:bg-[#F1E4D5]/50 align-top" data-testid={`task-row-${task.id}`}>
        <td className="px-4 py-3 whitespace-nowrap">{task.activity_date}</td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-1.5">
            {task.is_travel && <Plane className="w-3.5 h-3.5 text-[#EA823A]" aria-label="Travel" />}
            <span className="font-medium">{task.rate_name}</span>
          </div>
          {task.description && <div className="text-xs text-[#6B7A80] mt-0.5 max-w-md whitespace-pre-line">{task.description}</div>}
        </td>
        <td className="px-4 py-3 whitespace-nowrap">{Number(task.hours).toFixed(2)}</td>
        <td className="px-4 py-3 whitespace-nowrap">${Number(task.rate_amount).toFixed(2)}</td>
        <td className="px-4 py-3 whitespace-nowrap font-semibold text-[#1F2A2E]">${Number(task.amount).toFixed(2)}</td>
        <td className="px-4 py-3 text-xs text-[#6B7A80] whitespace-nowrap">{task.worker_name}</td>
        <td className="px-4 py-3"><StatusPill status={task.status} /></td>
        <td className="px-4 py-3 text-right whitespace-nowrap">
          <div className="flex justify-end gap-2">
            <button
              onClick={onToggleAttachments}
              className={`hover:text-[#E2718A] transition flex items-center gap-1 ${isAttachmentsOpen ? "text-[#E2718A]" : "text-[#284952]"}`}
              data-testid={`toggle-attachments-${task.id}`}
              aria-label="Attachments"
              title="Attachments"
            >
              <Paperclip className="w-4 h-4" />
              <ChevronDown className={`w-3 h-3 transition-transform ${isAttachmentsOpen ? "rotate-180" : ""}`} />
            </button>
            {task.status === "unbilled" && (
              <>
                <button onClick={onEdit} className="text-[#284952] hover:text-[#E2718A]" data-testid={`edit-task-${task.id}`} aria-label="Edit task">
                  <Edit2 className="w-4 h-4" />
                </button>
                <button onClick={onDelete} className="text-red-500 hover:text-red-700" data-testid={`delete-task-${task.id}`} aria-label="Delete task">
                  <Trash2 className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
        </td>
      </tr>
      {isAttachmentsOpen && (
        <tr className="bg-[#F1E4D5]/40" data-testid={`task-attachments-row-${task.id}`}>
          <td colSpan={8} className="px-4 py-4">
            <div className="max-w-2xl ml-12">
              <AttachmentManager scope="task" parentId={task.id} compact />
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
